import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { BUSINESSES, Business, Product } from './data';
export interface CategoryCard {
  id: string;
  title: string;
  subtitle: string;
  image: string;
}
export interface HeroContent {
  logo: string;
  badge: string;
  title: string;
  subtitle: string;
  backgroundImage: string;
  quickLinks: string[];
  searchCategories: string[];
}
export interface SiteContent {
  hero: HeroContent;
  categoryCards: CategoryCard[];
  categoriesHeading: string;
  partnersHeading: string;
  partnersSubheading: string;
}
export interface OrderRequest {
  id: string;
  reference?: string;
  createdAt: string;
  customerType: 'company' | 'private';
  logistics: 'delivery' | 'pickup';
  name: string;
  email: string;
  phone?: string;
  date: string;
  time?: string;
  address?: string;
  guests: string;
  notes: string;
  items: {
    name: string;
    businessName: string;
    quantity: number;
    price: number;
  }[];
  total: number;
  status: 'ny' | 'hanterad';
}
const DEFAULT_CONTENT: SiteContent = {
  hero: {
    logo: 'https://cdn.magicpatterns.com/uploads/6gty9bTseeZpLTSk5XdnDS/1.png',
    badge: 'Smaken som Baken',
    title: 'Småland samlat i ett.',
    subtitle: 'Det mesta bästa utvalt för alla tillfällen.',
    backgroundImage: 'https://cdn.magicpatterns.com/uploads/8zJoXUNp4411an513MnYZF/Skarmavbild_2026-07-30_kl._22.39.50.png',
    quickLinks: ['Frukost', 'Lunch & Middag', 'Fika', 'Afterwork', 'Foodtruck', 'Visa allt'],
    searchCategories: ['Frukost', 'Lunch & Middag', 'Fika', 'Bufféer', 'Foodtruck']
  },
  categoriesHeading: 'Populära kategorier',
  categoryCards: [{
    id: 'cat-1',
    title: 'Frukost',
    subtitle: 'Starta dagen på bästa sätt',
    image: 'https://cdn.magicpatterns.com/uploads/2npg1wVWKYWeGNKSb2o6pK/Skarmavbild_2026-07-30_kl._22.40.36.png'
  }, {
    id: 'cat-2',
    title: 'Lunch',
    subtitle: 'Sallader, bowls och varmrätter',
    image: 'https://cdn.magicpatterns.com/uploads/vPbgKrUML9EHTaUg8XEJNr/Skarmavbild_2026-07-30_kl._22.40.58.png'
  }, {
    id: 'cat-3',
    title: 'Fika',
    subtitle: 'Bullar, tårtor och kaffe',
    image: 'https://cdn.magicpatterns.com/uploads/cgaYWCx6ErAaDTzJ71Ykyu/Skarmavbild_2026-07-30_kl._22.41.18.png'
  }],
  partnersHeading: 'Utvalda partners',
  partnersSubheading: 'Handplockade restauranger och bagerier med högsta kvalitet.'
};
interface ContentContextType {
  businesses: Business[];
  content: SiteContent;
  requests: OrderRequest[];
  addBusiness: (business: Business) => void;
  updateBusiness: (slug: string, updates: Partial<Business>) => void;
  deleteBusiness: (slug: string) => void;
  addProduct: (slug: string, product: Product) => void;
  updateProduct: (slug: string, productId: string, updates: Partial<Product>) => void;
  deleteProduct: (slug: string, productId: string) => void;
  updateContent: (updates: Partial<SiteContent>) => void;
  addCategoryCard: (card: CategoryCard) => void;
  updateCategoryCard: (id: string, updates: Partial<CategoryCard>) => void;
  deleteCategoryCard: (id: string) => void;
  addRequest: (request: OrderRequest) => void;
  updateRequest: (id: string, updates: Partial<OrderRequest>) => void;
  deleteRequest: (id: string) => void;
  resetAll: () => void;
}
const ContentContext = createContext<ContentContextType | undefined>(undefined);
const STORAGE_KEY = 'ssb-platform-content-v1';
interface StoredState {
  businesses: Business[];
  content: SiteContent;
  requests: OrderRequest[];
}
function loadState(): StoredState {
  if (typeof window === 'undefined') {
    return {
      businesses: BUSINESSES,
      content: DEFAULT_CONTENT,
      requests: []
    };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {
      businesses: BUSINESSES,
      content: DEFAULT_CONTENT,
      requests: []
    };
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    return {
      businesses: parsed.businesses?.length ? parsed.businesses : BUSINESSES,
      content: {
        ...DEFAULT_CONTENT,
        ...(parsed.content ?? {}),
        hero: {
          ...DEFAULT_CONTENT.hero,
          ...(parsed.content?.hero ?? {})
        }
      },
      requests: parsed.requests ?? []
    };
  } catch {
    return {
      businesses: BUSINESSES,
      content: DEFAULT_CONTENT,
      requests: []
    };
  }
}
export function ContentProvider({
  children
}: {
  children: ReactNode;
}) {
  const initial = loadState();
  const [businesses, setBusinesses] = useState<Business[]>(initial.businesses);
  const [content, setContent] = useState<SiteContent>(initial.content);
  const [requests, setRequests] = useState<OrderRequest[]>(initial.requests);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        businesses,
        content,
        requests
      }));
    } catch {
      /* storage full or unavailable – ignore */
    }
  }, [businesses, content, requests]);
  const addBusiness = (business: Business) => setBusinesses(prev => [...prev, business]);
  const updateBusiness = (slug: string, updates: Partial<Business>) => setBusinesses(prev => prev.map(b => b.slug === slug ? {
    ...b,
    ...updates,
    products: (updates.products ?? b.products).map(p => ({
      ...p,
      businessSlug: updates.slug ?? b.slug,
      businessName: updates.name ?? b.name
    }))
  } : b));
  const deleteBusiness = (slug: string) => setBusinesses(prev => prev.filter(b => b.slug !== slug));
  const addProduct = (slug: string, product: Product) => setBusinesses(prev => prev.map(b => b.slug === slug ? {
    ...b,
    products: [...b.products, product]
  } : b));
  const updateProduct = (slug: string, productId: string, updates: Partial<Product>) => setBusinesses(prev => prev.map(b => b.slug === slug ? {
    ...b,
    products: b.products.map(p => p.id === productId ? {
      ...p,
      ...updates
    } : p)
  } : b));
  const deleteProduct = (slug: string, productId: string) => setBusinesses(prev => prev.map(b => b.slug === slug ? {
    ...b,
    products: b.products.filter(p => p.id !== productId)
  } : b));
  const updateContent = (updates: Partial<SiteContent>) => setContent(prev => ({
    ...prev,
    ...updates
  }));
  const addCategoryCard = (card: CategoryCard) => setContent(prev => ({
    ...prev,
    categoryCards: [...prev.categoryCards, card]
  }));
  const updateCategoryCard = (id: string, updates: Partial<CategoryCard>) => setContent(prev => ({
    ...prev,
    categoryCards: prev.categoryCards.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c)
  }));
  const deleteCategoryCard = (id: string) => setContent(prev => ({
    ...prev,
    categoryCards: prev.categoryCards.filter(c => c.id !== id)
  }));
  const addRequest = (request: OrderRequest) => setRequests(prev => [request, ...prev]);
  const updateRequest = (id: string, updates: Partial<OrderRequest>) => setRequests(prev => prev.map(r => r.id === id ? {
    ...r,
    ...updates
  } : r));
  const deleteRequest = (id: string) => setRequests(prev => prev.filter(r => r.id !== id));
  const resetAll = () => {
    setBusinesses(BUSINESSES);
    setContent(DEFAULT_CONTENT);
    setRequests([]);
  };
  return <ContentContext.Provider value={{
    businesses,
    content,
    requests,
    addBusiness,
    updateBusiness,
    deleteBusiness,
    addProduct,
    updateProduct,
    deleteProduct,
    updateContent,
    addCategoryCard,
    updateCategoryCard,
    deleteCategoryCard,
    addRequest,
    updateRequest,
    deleteRequest,
    resetAll
  }} data-id="element-728" data-mp="12p4onds201my">
      {children}
    </ContentContext.Provider>;
}
export function useContent() {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}